import { IsDateYmd } from "./dateHelper";

/**
 * Normalizes scalar or collection tag values.
 *
 * @param {string|Array<string>|null|undefined} value Tag value.
 * @returns {Array<string>} Unique normalized tags.
 */
export function NormalizeCartTags(value) {
    let source = value;

    if (
        typeof source === "string" &&
        source.trim().startsWith("[")
    ) {
        try {
            source = JSON.parse(source);
        } catch (error) {
            source = [source];
        }
    }

    if (!Array.isArray(source)) {
        source = source ? [source] : [];
    }

    return [...new Set(
        source
            .map((tag) => String(tag || "").trim())
            .filter(Boolean)
    )];
}

/**
 * Returns the encrypted album identifier from a related object.
 *
 * @param {Object|null} source Album, cart item, or media source.
 * @returns {string} Album key identifier or an empty string.
 */
export function GetAlbumKeyId(source = null) {
    return String(
        source?.AlbumKeyId ||
        source?.AlbumKey?.Id ||
        source?.park?.AlbumKey?.Id ||
        source?.Album?.AlbumKey?.Id ||
        ""
    ).trim();
}

/**
 * Builds a stable shopping-cart key.
 *
 * @param {Object} params Key parameters.
 * @param {string} params.kind Product kind.
 * @param {string|number|null} params.bookId Booking identifier.
 * @param {string|number|null} params.parkId Park identifier.
 * @param {string|number|null} params.mediaId Media identifier.
 * @param {string|null} params.serviceId Service identifier.
 * @param {string|null} params.albumKeyId Album key identifier.
 * @returns {string} Stable cart key.
 */
export function BuildCartKey({
    kind,
    bookId = null,
    parkId = null,
    mediaId = null,
    serviceId = null,
    albumKeyId = null,
}) {
    const normalizedKind = String(kind || "item");
    const identity = normalizedKind === "video"
        ? mediaId ?? bookId ?? parkId ?? "none"
        : bookId ?? parkId ?? mediaId ?? "none";

    const parts = [
        normalizedKind,
        identity,
        serviceId || "none",
        albumKeyId || "none",
    ];

    return parts.map((value) => String(value)).join(":");
}

/**
 * Finds the cart key associated with an album key identifier.
 *
 * @param {Object} shoppingCart Current shopping cart.
 * @param {string} albumKeyId Album key identifier.
 * @returns {string|undefined} Matching cart key.
 */
export function FindCartKeyByAlbumKey(shoppingCart, albumKeyId) {
    const normalizedAlbumKeyId = String(albumKeyId || "").trim();

    if (!normalizedAlbumKeyId) {
        return undefined;
    }

    return Object.keys(shoppingCart || {}).find((key) => {
        const item = shoppingCart[key];

        if (GetAlbumKeyId(item) === normalizedAlbumKeyId) {
            return true;
        }

        return Array.isArray(item?.Parks) && item.Parks.some(
            (park) => GetAlbumKeyId(park) === normalizedAlbumKeyId
        );
    });
}

/**
 * Builds ApiKoh tag entries from selected media.
 *
 * @param {Array<Object>} media Selected media items.
 * @param {number} parkId Park identifier.
 * @param {string} parkPrefix Park prefix.
 * @param {number} tagType Tag type.
 * @returns {Array<Object>} Unique tag and visit-date entries.
 */
export function BuildMediaTagEntries(
    media,
    parkId,
    parkPrefix,
    tagType = 1
) {
    const entries = [];
    const uniqueEntries = new Set();

    for (const item of Array.isArray(media) ? media : []) {
        const visitDate = String(item?.VisitDate || "").slice(0, 10);

        if (!IsDateYmd(visitDate)) {
            continue;
        }

        for (const tag of NormalizeCartTags(item?.Tags ?? item?.Tag)) {
            const uniqueKey = `${tag}|${visitDate}`;

            if (uniqueEntries.has(uniqueKey)) {
                continue;
            }

            uniqueEntries.add(uniqueKey);
            entries.push({
                tag,
                visitDate,
                udnPrefix: parkPrefix,
                parkId: Number(parkId),
                tagType: Number(tagType),
            });
        }
    }

    return entries;
}

/**
 * Checks whether the cart contains a compatible photo product for a video.
 *
 * @param {Object} shoppingCart Current shopping cart.
 * @param {Object} product Video product.
 * @returns {boolean} True when the video can use its upsell service.
 */
export function HasCompatiblePhotoProduct(shoppingCart, product) {
    const bookId = product?.Album?.Reservation?.BookId;
    const albumUuid = product?.AlbumUuid;
    const parkId = Number(product?.ParkId);

    return Object.values(shoppingCart || {}).some((item) => {
        if (Number(item?.type) === 3) {
            return false;
        }

        if (bookId !== undefined && bookId !== null) {
            return String(item?.BookId || "") === String(bookId);
        }

        if (albumUuid) {
            return String(item?.AlbumUuid || "") === String(albumUuid);
        }

        return Number(item?.ParkId) === parkId;
    });
}

/**
 * Resolves the commercial service used to quote a video.
 *
 * @param {Object} shoppingCart Current shopping cart.
 * @param {Object} product Video product.
 * @returns {string|null} Service identifier or null for location-based quoting.
 */
export function ResolveVideoQuotationService(shoppingCart, product) {
    const album = product?.Album || {};
    const quoting = album?.QuotingService || {};
    const hasCompatiblePhoto = HasCompatiblePhotoProduct(
        shoppingCart,
        product
    );

    if (hasCompatiblePhoto && quoting?.Upsell) {
        return String(quoting.Upsell).trim();
    }

    const defaultService =
        quoting?.Default ||
        album?.ServiceId ||
        album?.Service?.Id ||
        null;

    return defaultService ? String(defaultService).trim() : null;
}

/**
 * Determines whether a cart item belongs to an album experience.
 *
 * @param {Object} item Cart item.
 * @param {Object} albumExperience Album experience.
 * @returns {boolean} True when the item belongs to the experience.
 */
export function IsCartItemForAlbum(item, albumExperience) {
    const albumKeyId = GetAlbumKeyId(albumExperience);

    if (albumKeyId && GetAlbumKeyId(item) === albumKeyId) {
        return true;
    }

    if (albumKeyId && Array.isArray(item?.Parks)) {
        return item.Parks.some(
            (park) => GetAlbumKeyId(park) === albumKeyId
        );
    }

    if (albumKeyId) {
        return false;
    }

    const itemParkId = Number(
        item?.VideoDetails?.parkId ?? item?.ParkId
    );
    const experienceParkId = Number(
        albumExperience?.Park?.Id
    );

    if (
        !Number.isInteger(itemParkId) ||
        !Number.isInteger(experienceParkId) ||
        itemParkId !== experienceParkId
    ) {
        return false;
    }

    const itemServiceId = String(
        item?.ServiceId ||
        item?.VideoDetails?.serviceId ||
        ""
    ).trim();
    const experienceServiceId = String(
        albumExperience?.ServiceId ||
        albumExperience?.Service?.Id ||
        ""
    ).trim();

    if (
        itemServiceId &&
        experienceServiceId &&
        itemServiceId !== experienceServiceId
    ) {
        return false;
    }

    const itemVisitDate = String(
        item?.VisitDate || ""
    ).slice(0, 10);
    const experienceVisitDate = String(
        albumExperience?.VisitDate || ""
    ).slice(0, 10);

    return (
        !itemVisitDate ||
        !experienceVisitDate ||
        itemVisitDate === experienceVisitDate
    );
}

/**
 * Checks whether a video can be added under its video promotion rule.
 *
 * Missing, empty, or non-object promotion values mean that the video has no
 * quantity restriction. Photo-album promotion rules are not evaluated here.
 *
 * @param {Object} shoppingCart Current shopping cart.
 * @param {Object} videoAdded Quoted video product.
 * @returns {boolean} True when the video can be added.
 */
export function CanAddVideoByPromotion(shoppingCart, videoAdded) {
    const promotions = videoAdded?.Promotions;

    if (
        !promotions ||
        Array.isArray(promotions) ||
        typeof promotions !== "object"
    ) {
        return true;
    }

    const payLimit = Number(promotions.Pay);

    if (!Number.isFinite(payLimit) || payLimit <= 0) {
        return true;
    }

    const serviceId = String(
        videoAdded?.VideoDetails?.serviceId || ""
    ).trim();
    const matchingVideos = Object.values(shoppingCart || {}).filter(
        (item) =>
            Number(item?.type) === 3 &&
            String(item?.VideoDetails?.serviceId || "").trim() ===
                serviceId
    );

    return matchingVideos.length < payLimit;
}
